import { Deserializer } from "jsonapi-serializer";
import {
    INTERNAL_SERVER_ERROR,
    NO_CONTENT,
} from "../constants/response-status-codes";
import { AccessProfile } from "../models/AccessProfile";
import { genericError } from "../models/GenericError";
import { ListQuery } from "../models/ListQuery";
import { Name } from "../models/Name";
import { PaginationModel } from "../models/Pagination";
import { DeleteEntityResult } from "../models/services/DeleteEntityResult";
import { StoreEntityResult } from "../models/services/StoreEntityResult";
import { AccessProfileWrapper } from "../models/wrappers/AccessProfileWrapper";
import { APIConnector } from "./api/APIConnector";
import { createPagination } from "./utilities/paginationBuilder";
import { processServerError, getConstraintViolation } from "./utilities/ServiceError";
import { createPartialURL, getFilterClause } from "./utilities/urlBuilder";

const apiConnector = new APIConnector();

const buildAccessProfile = (apiAccessProfile: any): AccessProfile => {
    return {
        id: apiAccessProfile["id"],
        name: apiAccessProfile["ap-name"],
        description: apiAccessProfile["ap-desc"],
        enabled: apiAccessProfile["ap-enabled"],
        totalGroups: apiAccessProfile["total-access-groups"],
        totalFloorGroups: apiAccessProfile["total-floor-groups"],
        totalMasters: apiAccessProfile["total-masters"],
    };
};

export const createAccessProfile = async (accessProfile: AccessProfile) => {
    try {
        const record = {
            data: {
                type: 'accessProfile',
                id: accessProfile.id,
                attributes: {
                    apName: accessProfile.name,
                    apDesc: accessProfile.description,
                    apEnabled: accessProfile.enabled
                }
            }
        };

        let response: Response;
        const url = "accessProfile";

        await apiConnector.sendData(url, true, record).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                const content = await response.json();
                if (content.errors) {
                    return reject({ status: response.status, message: content.errors.map(error => getConstraintViolation(error.detail)) })
                }

                return new Deserializer().deserialize(content, (error, record) => {
                    if (error) {
                        return reject({ status: response.status, id: record.id, success: false, message: genericError });
                    }

                    return resolve({ id: record.id, success: true });
                })
            }

            return reject({ status: INTERNAL_SERVER_ERROR, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const deleteAccessProfile = async (id: string): Promise<DeleteEntityResult> => {
    try {
        const url = `accessProfile/${id}`;
        const accessProfile = {
            data: {
                type: "accessProfile",
                id: id,
            },
        };

        const response = await apiConnector.deleteData(url, accessProfile);

        return new Promise(async (resolve, reject) => {
            if (response.status === NO_CONTENT) {
                return resolve({ id: id, success: true });
            }

            const json = await response.json();

            return reject({status: response.status, id: id, success: false, message: getConstraintViolation(json.errors[0].detail)});
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAllAccessProfiles = async (listQuery?: ListQuery): Promise<{ rows: AccessProfile[]; pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `accessProfile?${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, accessProfiles: any[]) => {
                if (err) {
                    return reject({
                        status: response.status,
                        message: genericError,
                    });
                }

                const accessProfile: AccessProfile[] = accessProfiles.map(buildAccessProfile);

                return resolve({ rows: accessProfile, pagination });
            },
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAccessProfileById = async (id: string): Promise<AccessProfile> => {
    try {
        const url = `accessProfile/${id}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) });
            }
            new Deserializer().deserialize(json, (err: any, apiAccessProfile: any) => {
                    if (err) {
                        return reject({ status: response.status, message: genericError });
                    }
                    const accessProfile = buildAccessProfile(apiAccessProfile);

                    return resolve(accessProfile);
                },
            );
        });
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
};

export const getAccessProfilesNamesWithPagination = async (listQuery?: ListQuery): Promise<{ rows: Name[], pagination: PaginationModel }> => {
    try {
        const filters: string = getFilterClause(listQuery?.filters, listQuery?.searchTerm);
        const paginationString = createPartialURL(listQuery?.goToPage, listQuery?.pageSize, listQuery?.sortBy, filters);
        const url = `accessProfile?${paginationString}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const newPagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, apiAccessGroups: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const accessGroups: Name[] = apiAccessGroups.map((accessGroup): Name => {
                    return {
                        value: accessGroup['id'],
                        label: accessGroup['ap-name'],
                    };
                });

                return resolve({ rows: accessGroups, pagination: newPagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const getAccessProfilesForPerson = async (masterId: number | string, listParameters?: ListQuery): Promise<AccessProfileWrapper> => {
    try {
        const masterFilters = `master.id==${masterId}`;
        let filters: string = getFilterClause(listParameters?.filters, listParameters?.searchTerm);
        filters = filters ? `${filters};${masterFilters}` : `&filter=${masterFilters}`; 
        const partialURL = createPartialURL(listParameters?.goToPage, listParameters?.pageSize, listParameters?.sortBy, filters);
        const masterGroupProfileFields = 'fields[masterGroupProfile]=accessProfile';
        const accessProfileFields = 'fields[accessProfile]=apName,apDesc,apEnabled,totalAccessGroups,totalFloorGroups,totalMasters';
        const url = `masterGroupProfile?include=accessProfile${partialURL}&${masterGroupProfileFields}&${accessProfileFields}`;
        const response = await apiConnector.fetchData(url);
        const json = await response.json();
        const pagination = createPagination(json.meta);

        return new Promise((resolve, reject) => {
            if (json.errors) {
                return reject({ status: response.status, message: getConstraintViolation(json.errors[0].detail) })
            }
            new Deserializer().deserialize(json, (err: any, masterGroups: any[]) => {
                if (err) {
                    return reject({ status: response.status, message: genericError })
                }

                const accessProfiles: AccessProfile[] = masterGroups.map((personAccessGroup) => buildAccessProfile(personAccessGroup['access-profile']));

                return resolve({ rows: accessProfiles, pagination });
            })
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
}

export const editAccessProfile = async (accessProfile: AccessProfile): Promise<StoreEntityResult> => {
    try {
        const record = {
            data: {
                type: "accessProfile",
                id: accessProfile.id,
                attributes: {
                    apName: accessProfile.name,
                    apDesc: accessProfile.description,
                    apEnabled: accessProfile.enabled,
                },
            }
        };

        const url = `accessProfile/${accessProfile.id}`;
        let response: Response;

        await apiConnector.sendData(url, false, record).then(data => {
            response = data;
        }).catch(error => {
            return new Promise((resolve, reject) => {
                return reject(processServerError(error));
            })
        });

        return new Promise<StoreEntityResult>(async (resolve, reject) => {
            if (response) {
                if (response.status === NO_CONTENT) {
                    return resolve({ id: record.data.id, success: true })
                }

                const content = await response.json();
                return reject({ status: response.status, id: record.data.id, success: false, message: content.errors.map(error => getConstraintViolation(error.detail)) });
            }

            return reject({ status: INTERNAL_SERVER_ERROR, id: record.data.id, success: false, message: genericError });
        })
    } catch (error) {
        return new Promise((resolve, reject) => {
            reject(processServerError(error));
        });
    }
} 
